import React, { useEffect, useRef } from 'react';

interface ReaderShadowHostReadyPayload {
  shadowRoot: ShadowRoot;
  readerRoot: HTMLDivElement;
  popupRoot: HTMLDivElement;
}

interface ReaderShadowHostProps {
  className?: string;
  isDarkMode?: boolean;
  onReady: (payload: ReaderShadowHostReadyPayload) => void;
}

const INTERACTION_STYLE_ID = 'reader-interaction-styles';
const READER_ROOT_ID = 'reader-root';
const READER_POPUP_ROOT_ID = 'reader-popup-root';
const READER_SHADOW_MODE: ShadowRootMode = 'closed';

const READER_INTERACTION_CSS = `
.reader-ebook-document img {
  height: auto;
}

@media (hover: hover) and (pointer: fine) {
  .reader-word-sync--interactive [data-w],
  .reader-word-sync--interactive [data-w-part] {
    cursor: pointer;
  }

  .reader-word-sync--interactive [data-w]:hover,
  .reader-word-sync--interactive [data-w-part]:hover {
    background-color: rgba(37, 99, 235, 0.12);
    border-radius: 0.2rem;
  }
}

.reader-progress-layer-host {
  isolation: isolate;
}

.reader-progress-overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: -1;
}

.reader-progress-overlay-rect {
  position: absolute;
  background-color: rgba(37, 99, 235, 0.18);
  border-radius: 0.2rem;
}

.reader-chapter-slot {
  display: flow-root;
  overflow-anchor: none;
}

.reader-chapter-slot[data-chapter-mounted="false"] {
  pointer-events: none;
}

[data-reader-navigation-target="true"] {
  outline: 2px solid rgba(37, 99, 235, 0.55);
  outline-offset: 0.25rem;
}

:host(.reader-shadow-host--dark) .reader-progress-overlay-rect {
  background-color: rgba(86, 118, 255, 0.24);
}

@media (prefers-reduced-motion: reduce) {
  :host, .reader-ebook-document {
    scroll-behavior: auto;
  }
}

#${READER_POPUP_ROOT_ID} {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 50;
}

.reader-endnote-popup {
  position: absolute;
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  width: 20rem;
  max-width: min(20rem, calc(100vw - 2rem));
  max-height: min(20rem, calc(100vh - 2rem));
  border-radius: 0.75rem;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background-color: #ffffff;
  color: #1f2430;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.16), 0 2px 8px rgba(15, 23, 42, 0.08);
  font-size: 0.9rem;
  line-height: 1.5;
  overflow: hidden;
}

:host(.reader-shadow-host--dark) .reader-endnote-popup {
  border-color: rgba(255, 255, 255, 0.1);
  background-color: #24293a;
  color: #e7e9f0;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5), 0 2px 8px rgba(0, 0, 0, 0.3);
}

.reader-endnote-popup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.375rem 0.5rem 0.375rem 1rem;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  flex-shrink: 0;
}

:host(.reader-shadow-host--dark) .reader-endnote-popup-header {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.reader-endnote-popup-title {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  opacity: 0.55;
}

.reader-endnote-popup-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  border: none;
  border-radius: 9999px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 120ms ease-out;
}

.reader-endnote-popup-close:focus-visible {
  background-color: rgba(15, 23, 42, 0.08);
}

:host(.reader-shadow-host--dark) .reader-endnote-popup-close:focus-visible {
  background-color: rgba(255, 255, 255, 0.12);
}

.reader-endnote-popup-close svg {
  width: 1rem;
  height: 1rem;
}

.reader-endnote-popup-body {
  padding: 0.75rem 1rem 1rem;
  overflow-y: auto;
}

.reader-endnote-popup-content :first-child {
  margin-top: 0;
}

.reader-endnote-popup-content :last-child {
  margin-bottom: 0;
}

.reader-endnote-popup-content p {
  margin: 0 0 0.6em;
}

/* Note markup is typically a cloned <li> pulled out of the endnotes chapter's
   <ol>/<ul> -- outside that list context it would otherwise still render its
   default disc/decimal marker. */
.reader-endnote-popup-content li {
  display: block;
  list-style: none;
}

.reader-endnote-popup-content a {
  color: #2f45ff;
  text-decoration: underline;
  text-underline-offset: 2px;
}

:host(.reader-shadow-host--dark) .reader-endnote-popup-content a {
  color: #85a8ff;
}

.reader-endnote-popup-loading,
.reader-endnote-popup-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  opacity: 0.7;
}

.reader-endnote-popup-spinner {
  width: 0.9rem;
  height: 0.9rem;
  border-radius: 9999px;
  border: 2px solid rgba(15, 23, 42, 0.18);
  border-top-color: rgba(15, 23, 42, 0.6);
  animation: reader-endnote-popup-spin 700ms linear infinite;
  flex-shrink: 0;
}

:host(.reader-shadow-host--dark) .reader-endnote-popup-spinner {
  border-color: rgba(255, 255, 255, 0.2);
  border-top-color: rgba(255, 255, 255, 0.75);
}

@keyframes reader-endnote-popup-spin {
  to { transform: rotate(360deg); }
}

.reader-endnote-popup-error {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.reader-endnote-popup-error p {
  margin: 0;
}

.reader-endnote-popup-retry {
  align-self: flex-start;
  padding: 0.35rem 0.9rem;
  border-radius: 9999px;
  border: 1px solid rgba(15, 23, 42, 0.2);
  background: transparent;
  color: inherit;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 120ms ease-out;
}

.reader-endnote-popup-retry:hover,
.reader-endnote-popup-retry:focus-visible {
  background-color: rgba(15, 23, 42, 0.06);
}

:host(.reader-shadow-host--dark) .reader-endnote-popup-retry {
  border-color: rgba(255, 255, 255, 0.25);
}

:host(.reader-shadow-host--dark) .reader-endnote-popup-retry:hover,
:host(.reader-shadow-host--dark) .reader-endnote-popup-retry:focus-visible {
  background-color: rgba(255, 255, 255, 0.1);
}

@media (prefers-reduced-motion: reduce) {
  .reader-endnote-popup-spinner {
    animation: none;
  }
}

@keyframes reader-endnote-popup-enter {
  from { opacity: 0; transform: translateY(-2px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

@keyframes reader-endnote-popup-exit {
  from { opacity: 1; transform: translateY(0) scale(1); }
  to { opacity: 0; transform: translateY(-2px) scale(0.98); }
}

.reader-endnote-popup-enter {
  animation: reader-endnote-popup-enter 120ms ease-out;
}

.reader-endnote-popup-exit {
  animation: reader-endnote-popup-exit 120ms ease-in forwards;
}

@media (prefers-reduced-motion: reduce) {
  .reader-endnote-popup-enter, .reader-endnote-popup-exit {
    animation: none;
  }
}
`;

const ReaderShadowHost: React.FC<ReaderShadowHostProps> = ({ className, isDarkMode = false, onReady }) => {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const shadowRootRef = useRef<ShadowRoot | null>(null);
  const readerRootRef = useRef<HTMLDivElement | null>(null);
  const popupRootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return;
    }

    let shadowRoot = shadowRootRef.current;
    if (!shadowRoot || shadowRoot.host !== host) {
      shadowRoot = host.attachShadow({ mode: READER_SHADOW_MODE });
      shadowRootRef.current = shadowRoot;
      readerRootRef.current = null;
      popupRootRef.current = null;
    }

    let interactionStyle = shadowRoot.getElementById(INTERACTION_STYLE_ID) as HTMLStyleElement | null;
    if (!interactionStyle) {
      interactionStyle = document.createElement('style');
      interactionStyle.id = INTERACTION_STYLE_ID;
      shadowRoot.appendChild(interactionStyle);
    }
    // Always resync content (not just on creation) so a stale style tag left
    // over from a previous mount/HMR update can't keep serving old CSS.
    interactionStyle.textContent = READER_INTERACTION_CSS;

    let readerRoot = readerRootRef.current;
    if (readerRoot && readerRoot.parentNode !== shadowRoot) {
      readerRoot = null;
    }
    if (!readerRoot) {
      readerRoot = shadowRoot.getElementById(READER_ROOT_ID) as HTMLDivElement | null;
    }
    if (!readerRoot) {
      readerRoot = document.createElement('div');
      readerRoot.id = READER_ROOT_ID;
      readerRoot.className = 'reader-ebook-document';
      shadowRoot.appendChild(readerRoot);
    }
    readerRootRef.current = readerRoot;

    let popupRoot = popupRootRef.current;
    if (popupRoot && popupRoot.parentNode !== shadowRoot) {
      popupRoot = null;
    }
    if (!popupRoot) {
      popupRoot = shadowRoot.getElementById(READER_POPUP_ROOT_ID) as HTMLDivElement | null;
    }
    if (!popupRoot) {
      popupRoot = document.createElement('div');
      popupRoot.id = READER_POPUP_ROOT_ID;
      shadowRoot.appendChild(popupRoot);
    }
    popupRootRef.current = popupRoot;

    onReady({ shadowRoot, readerRoot, popupRoot });

    return () => {
      shadowRoot.replaceChildren();
      readerRootRef.current = null;
      popupRootRef.current = null;
    };
  }, [onReady]);

  return (
    <div
      ref={hostRef}
      className={`${className ?? ''}${className ? ' ' : ''}${isDarkMode ? 'reader-shadow-host--dark' : ''}`}
    />
  );
};

export default ReaderShadowHost;
